import { Check, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { useState } from 'react';
import { Client, Connection } from '@/types/api';
import Card from '../ui/Card';
import { IconButton } from '../ui/IconButton';
import { cn } from '@/lib/utils';
import { Input } from '../ui/Input';
import { H3 } from '../ui/Typography';
import { Body } from '../ui/Typography';
import { LoadingSpinner } from '../LoadingSpinner';

interface BudgetCardProps {
  id: string;
  name: string;
  client?: Client,
  connection?: Connection,
  isActive?: boolean;
  isDeleting?: boolean;
  isSettingActive?: boolean;
  isSelected: boolean;
  onSetActive: () => void;
  onDelete: () => void;
  onSelect: () => void;
  onUpdateName: (newName: string) => void;
}

export const BudgetCard = ({
  name,
  client,
  connection,
  isActive,
  isDeleting,
  isSettingActive,
  isSelected,
  onSetActive,
  onDelete,
  onSelect,
  onUpdateName,
}: BudgetCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);

  const handleDelete = () => {
    setIsDeleteDialogOpen(false);
    onDelete();
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    onUpdateName(editedName);
    setIsEditing(false);
  };

  if (isDeleting) {
    return (
      <Card>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <LoadingSpinner message="Deleting budget..." />
          </div>
        </div>
      </Card>
    );
  }
  if (isSettingActive) {
    return (
      <Card>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <LoadingSpinner message="Setting budget as active..." />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        className={cn(
          isSelected ? "border-border-dark" : "border-border-default",
          "cursor-pointer"
        )}
        onClick={onSelect}
      >
        <div className="grid grid-cols-2">
          <div className="flex-grow">
            {isEditing ? (
              <Input
                className="w-full p-2"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <H3>{name}</H3>
            )}
          </div>

          <div className="flex gap-1 justify-end items-center">
            {isEditing ? (
              <>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveClick();
                  }}
                  color='success'
                  className="hover:bg-success hover:text-palette-white"
                >
                  <Check className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(false);
                  }}
                  className="hover:bg-error hover:text-palette-white"
                >
                  <X className="h-3.5 w-3.5" />
                </IconButton>
              </>
            ) : (
              <div className="flex gap-1 justify-end items-center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick();
                  }}
                  className="text-palette-gray hover:bg-background-default"
                >
                  <Edit className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(true);
                  }}
                  className="text-palette-gray hover:bg-error hover:text-palette-white disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end items-center pt-4">
          {isActive ? (
            <Body size='small' className="text-primary flex items-center gap-2">
              Active <Check strokeWidth={5} className="h-4 w-4" />
            </Body>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={onSetActive}
              color="primary"
            >
              Set as Active
            </Button>
          )}
        </div>
      </Card >

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Budget"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete this budget? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};